import { Component, computed, DestroyRef, inject, input, model, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideLoaderCircle, lucideSearch } from '@ng-icons/lucide';
import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { hlm } from '@spartan-ng/helm/utils';
import { remove as removeDiac } from 'diacritics';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { Cartographie } from '../../../cartographie/models/cartographie.model';
import { CartographieStore } from '../../../cartographie/store/cartographie.store';
import { Module } from '../../models/module.model';
import { Parcours } from '../../models/parcours.model';
import { Étape } from '../../models/étape.model';
import { ModuleService } from '../../parcours-module/parcours-module.service';
import { ModuleStore } from '../../parcours-module/store/module.store';
import { RéférentielMétierParcoursService } from '../../services/référentiel-métier-parcours.service';
import { ItemModuleComponent } from './item-module/item-module.component';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';
import { RéférentielService } from '../../../administration/gestion-référentiels/référentiel.service';

@Component({
  selector: 'app-import-module',
  imports: [
    ExpansionPanel,
    LabelRequiredDirective,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    ReactiveFormsModule,
    TrierParPipe,
    FormsModule,
    HlmSeparatorImports,
    HlmIconImports,
    HlmInputGroupImports,
    HlmRadioGroupImports,
    ItemModuleComponent,
    HlmSpinnerImports,
  ],
  templateUrl: './import-module.component.html',
  providers: [provideIcons({ lucideSearch, lucideLoaderCircle })],
})
export class ImportModuleComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #référentielService = inject(RéférentielService);
  readonly #parcoursService = inject(RéférentielMétierParcoursService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #moduleService = inject(ModuleService);
  readonly #moduleStore = inject(ModuleStore);

  readonly etape = input.required<Étape>();

  readonly cartographies = this.#cartographieStore.entities;
  readonly referentiels = signal([] as Référentiel[]);
  readonly parcours = signal([] as Parcours[]);
  readonly modules = signal([] as Module[]);

  readonly form = this.#fb.group({
    cartographie: [null, [Validators.required]],
    referentiel: [null, [Validators.required]],
    parcours: [null, [Validators.required]],
  });

  rechercheModule = model('');
  modulesFiltres = computed(() =>
    this.modules().filter(
      (x) =>
        removeDiac(x.nom ?? '')
          .toLowerCase()
          .includes(removeDiac(this.rechercheModule().toLowerCase())) || this.moduleSelectionne()?.id === x.id,
    ),
  );
  moduleSelectionne = model<Module | null>(null);

  readonly isLoading = computed(() => this.#sidepanelService.loading());

  public readonly cardClass = hlm(
    'relative block space-x-0',
    // base card styles
    'gap-md outline-stroke-neutral-light flex flex-col rounded-lg outline',
    // hover and background styles
    'bg-components-background-neutral-lightest hover:bg-components-background-neutral-lightest cursor-pointer transition-colors hover:outline-2 hover:outline-stroke-neutral-medium',
    // target the checked state properly
    '[&:has([data-checked=true])]:outline-2 [&:has([data-checked=true])]:outline-stroke-primary-main',
  );

  ngOnInit(): void {
    this.#cartographieStore.charger();

    this.cartographie.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged(),
        filter((x) => !!x),
        switchMap((cartographie) => this.#référentielService.findAll(cartographie.id)),
        map((x) => x.items),
      )
      .subscribe((référentiels: Référentiel[]) => {
        this.referentiels.set(référentiels);
        // Lorsqu'on change de cartographie, on réinitialise les référentiels, les parcours ainsi que les modules
        this.referentielFC.setValue(null);
        this.parcours.set([]);
        this.parcoursFC.setValue(null);
        this.modules.set([]);
      });

    this.referentielFC.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        distinctUntilChanged(),
        filter((x) => !!x),
        switchMap((référentiel) =>
          this.#parcoursService.trouverParGroupe(this.cartographie.value!.id, référentiel.idGroupe),
        ),
        map((x) => x.items),
      )
      .subscribe((parcours: Parcours[]) => {
        this.parcours.set(parcours);
        // Lorsqu'on change de référentiel, on réinitialise les parcours ainsi que les modules
        this.parcoursFC.setValue(null);
        this.modules.set([]);
      });

    this.parcoursFC.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((x) => !!x),
        switchMap((parcours) => this.#moduleService.trouverTous(parcours.id)),
        map((x) => x.items),
      )
      .subscribe((modules: Module[]) => this.modules.set(modules));

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      const module = this.moduleSelectionne();
      if (module) {
        this.#moduleStore.dupliquer({
          idModule: module.id,
          idÉtape: this.etape().id,
          next: () => this.#sidepanelService.close(),
        });
      }
    });
  }

  get cartographie() {
    return this.form.get('cartographie') as AbstractControl<Cartographie | null>;
  }

  get referentielFC() {
    return this.form.get('referentiel') as AbstractControl<Référentiel | null>;
  }

  get parcoursFC() {
    return this.form.get('parcours') as AbstractControl<Parcours | null>;
  }
}
